import { action, observable } from 'mobx';
import { api } from '../config';

// Helpers
import { getCookie, getPath } from '../helpers/helpers';

// Components
import SessionStore from './SessionStore';
import ReportStore from './ReportStore';

var obx = observable({
  transactionsNext: null,
  transactions: [],
  summary: {},
  examConfigurations:[],
  courseOfferings:[],
  stats:[],
  configurationCount: 0,
  // Get install exam configurations
  getExamConfigurations: action(function() {
    SessionStore.startLoading('Loading…', 'Configuration');

    let headers = {};

    if(SessionStore.hasSessionStorage) {
      headers = {
        'token': sessionStorage.getItem('token')
      }
    } else {
      headers = {
        'token': getCookie('token')
      }
    }

    api.get(`/installs/${SessionStore.userData.installSid}/unapprovedConfigurations`, { headers })
      .then(response => {
        obx.examConfigurations = response.data;
        obx.configurationCount = response.data.total
        SessionStore.finishLoading('Configuration');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get installation proctor approvals
  approvalsNext: null,
  approvals:[],
  approvalCount: 0,
  approvalsFilter: {},
  getInstallApprovals: action(function(type) {
    SessionStore.startLoading('Loading…', 'getInstallApprovals');

    let headers = {};

    if(SessionStore.hasSessionStorage) {
      headers = {'token': sessionStorage.getItem('token')}
    } else {
      headers = {'token': getCookie('token')}
    }

    let query = '?limit=25';

    if(Object.keys(obx.approvalsFilter).length > 0) {
      for(var key in obx.approvalsFilter) {
        if(obx.approvalsFilter[key]) {
          query += `&${key}=${obx.approvalsFilter[key]}`
        }
      }
    }

    api.get(`/installs/${SessionStore.userData.installSid}/approvals${query}`, { headers })
      .then(response => {
        obx.approvals = response.data.results;
        obx.approvalCount = response.data.total;

        // Set pagination next url
        if(response.data.next) {
          // API returns a url with a different domain for the next link
          // so create an anchor element and set its href to the next link
          // and then grab the pathname and query from that
          let nextUrl = getPath(response.data.next);
          nextUrl = nextUrl.replace('/v1', '');
          obx.approvalsNext = nextUrl;
        } else {
          obx.approvalsNext = null;
        }

        SessionStore.finishLoading('getInstallApprovals');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get installation overview stats
  getOverviewStats: action(function() {
    SessionStore.startLoading('Loading…', 'getOverviewStats');

    let headers = {};

    if(SessionStore.hasSessionStorage) {
      headers = {'token': sessionStorage.getItem('token')}
    } else {
      headers = {'token': getCookie('token')}
    }

    api.get(`/installs/${SessionStore.userData.installSid}/reports/aggregatedStats`, { headers })
      .then(response => {
        obx.stats = response.data.results.map(x => x.data);
        SessionStore.finishLoading('getOverviewStats');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get installation course offerings
  getCourseOfferings: action(function() {
    SessionStore.startLoading('Loading…', 'courseOfferings');

    api.get(`/installs/${SessionStore.userData.installSid}/courseOfferings`)
      .then(response => {
        obx.courseOfferings = response.data.results
        SessionStore.finishLoading('courseOfferings');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get institution settings
  institutionSettings: {},
  getDefaultCourseOffering: action(function() {
    SessionStore.startLoading('Loading…', 'getDefaultCourseOffering');

    api.get(`/installs/${SessionStore.userData.installSid}/settings`)
      .then(response => {
        obx.institutionSettings = response.data;

        SessionStore.finishLoading('getDefaultCourseOffering');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get installation transactions
  ledgerFilters: {},
  getTransactions: action(function() {
    SessionStore.startLoading('Loading…', 'Ledger');

    let query = '?limit=25';

    if(Object.keys(obx.ledgerFilters).length > 0) {
      for(var key in obx.ledgerFilters) {
        if(obx.ledgerFilters[key]) {
          query += `&${key}=${obx.ledgerFilters[key]}`
        }
      }
    }

    // Store the report URL so we can use it to generate a CSV if we need to
    ReportStore.reportUrl = `/installs/${SessionStore.userData.installSid}/ledger${query}`;

    api.get(`/installs/${SessionStore.userData.installSid}/ledger${query}`)
      .then(response => {
        obx.transactions = response.data.results;
        obx.summary = response.data.summary;

        // Set pagination next url
        if(response.data.next) {
          // API returns a url with a different domain for the next link
          // so create an anchor element and set its href to the next link
          // and then grab the pathname and query from that
          let nextUrl = getPath(response.data.next);
          nextUrl = nextUrl.replace('/v1', '');
          obx.transactionsNext = nextUrl;
        } else {
          obx.transactionsNext = null;
        }

        // Store the formatting in case we need to generate a CSV
        ReportStore.reportDefinition = {
          "Date": "{{createdDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
          "Voucher Code": "{% if voucher %}{{voucher.code | customEscape}}{% endif %}",
          "Voucher Amount": "{% if voucher %}{{voucher.amount | customEscape}} {{voucher.currency | customEscape}}{% endif %}",
          "Funding Rule": "{% if fundingRule %}{{fundingRule.title | customEscape}}{% endif %}",
          "Funding Rule Type": "{% if fundingRule %}{{fundingRule.appliesTo | customEscape}}{% endif %}",
          "Funding Rule Amount": "{% if fundingRule %}{{fundingRule.amount | customEscape}}{% if fundingRule.units == '%'%}%{% else %} {{fundingRule.currency | customEscape}}{% endif %}{% endif %}",
          "Exam Session": "{% if examSession %}{{exam.title | customEscape}}; {{course.title | customEscape}}{% endif %}",
          "Exam Session ID": "{% if examSession %}{{examSession.sid | customEscape}}{% endif %}",
          "Exam Session Status": "{% if examSession %}{{examSession.status | customEscape}}{% endif %}",
          "Exam Session Date": "{{examSession.appointmentDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
          "Student": "{% if student %}{{student.firstName | customEscape}} {{student.lastName | customEscape}}{% endif %}",
          "Student address": "{% if student %}{{student.address | customEscape}}{% endif %}",
          "Student latitude": "{% if student %}{{student.latitude | customEscape}}{% endif %}",
          "Student longitude": "{% if student %}{{student.longitude | customEscape}}{% endif %}",
          "Charge": "{{amount | customEscape}}",
          "Currency": "{{currency | customEscape}}"
        }

        SessionStore.finishLoading('Ledger');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  })
});

export default obx;



// WEBPACK FOOTER //
// ./src/app/stores/AdminStore.js